import React from 'react';

const KeywordMetricsPage = () => {
  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Keyword Metrics</h2>
        <p className="text-gray-600 mt-1">Analyze and optimize your keywords to improve product visibility</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Card 1 */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Total Keywords</p>
                    <h3 className="text-2xl font-bold text-gray-800 mt-1">1,856</h3>
                </div>
                <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-key text-indigo-600"></i>
                </div>
            </div>
            <div className="mt-4 flex items-center">
                <span className="text-green-500 text-sm font-medium flex items-center">
                    <i className="fas fa-arrow-up mr-1"></i> 5.2%
                </span>
                <span className="text-gray-500 text-sm ml-2">from last month</span>
            </div>
        </div>
        {/* Card 2 */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Avg. Search Volume</p>
                    <h3 className="text-2xl font-bold text-gray-800 mt-1">4.8K</h3>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-search text-green-600"></i>
                </div>
            </div>
            <div className="mt-4 flex items-center">
                <span className="text-green-500 text-sm font-medium flex items-center">
                    <i className="fas fa-arrow-up mr-1"></i> 3.1%
                </span>
                <span className="text-gray-500 text-sm ml-2">from last month</span>
            </div>
        </div>
        {/* Card 3 */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Avg. Keyword Cost</p>
                    <h3 className="text-2xl font-bold text-gray-800 mt-1">$1.24</h3>
                </div>
                <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-dollar-sign text-yellow-600"></i>
                </div>
            </div>
            <div className="mt-4 flex items-center">
                <span className="text-red-500 text-sm font-medium flex items-center">
                    <i className="fas fa-arrow-up mr-1"></i> 2.4% {/* Note: arrow up but red color in original? Check logic */}
                </span>
                <span className="text-gray-500 text-sm ml-2">from last month</span>
            </div>
        </div>
        {/* Card 4 */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Optimization Opportunities</p>
                    <h3 className="text-2xl font-bold text-gray-800 mt-1">74</h3>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-magic text-blue-600"></i>
                </div>
            </div>
            <div className="mt-4 flex items-center">
                <span className="text-green-500 text-sm font-medium flex items-center">
                    <i className="fas fa-arrow-down mr-1"></i> 12%
                </span>
                <span className="text-gray-500 text-sm ml-2">from last month</span>
            </div>
        </div>
      </div>

      {/* Keyword Performance Chart */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-800">Keyword Performance Trend</h3>
          <div className="flex items-center space-x-3">
            <select className="border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
              <option>Last 90 days</option>
            </select>
            <button className="text-sm text-indigo-600 hover:text-indigo-800">Export</button>
          </div>
        </div>
        <div className="chart-container">
          {/* Chart component will go here */}
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
            <div className="text-center">
              <p className="text-gray-500">Keyword Performance Chart Placeholder</p>
              <p className="text-xs text-gray-400 mt-1">CTR and traffic data over time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Keyword Metrics Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Keyword Metrics</h3>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <input type="text" placeholder="Search keywords..." className="border border-gray-300 rounded-md pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm w-60" />
              <i className="fas fa-search absolute left-2.5 top-2.5 text-gray-400"></i>
            </div>
            <select className="border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm">
              <option>All Stores</option>
              <option>Urban Gadgets</option>
              <option>Lifestyle Shop</option>
              <option>EcoProducts</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search Volume</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competition</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. CPC</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Links</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your CTR</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Example Row 1 */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">wireless earbuds</div></td>
                <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">12,500</div></td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="w-24 bg-gray-200 rounded-full h-2.5">
                    <div className="bg-yellow-500 h-2.5 rounded-full" style={{ width: '65%' }}></div> {/* Converted style */}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Medium (0.65)</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">$1.85</div></td>
                <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">32</div></td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-green-500 font-medium">4.8%</div>
                  <div className="text-xs text-gray-500">Avg: 3.2%</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Good</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button className="text-gray-600 hover:text-gray-900">View Details</button>
                </td>
              </tr>
              {/* Add more example rows here */}
              <tr>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">bluetooth speaker portable</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">8,750</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-24 bg-gray-200 rounded-full h-2.5">
                          <div className="bg-red-500 h-2.5 rounded-full" style={{width: '82%'}}></div> {/* Converted style */}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">High (0.82)</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">$2.15</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">45</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-500 font-medium">1.2%</div>
                      <div className="text-xs text-gray-500">Avg: 2.8%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Underperforming</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-2">Optimize</button>
                      <button className="text-gray-600 hover:text-gray-900">View Details</button>
                  </td>
              </tr>
              <tr>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">organic cotton t-shirt</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">5,200</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-24 bg-gray-200 rounded-full h-2.5">
                          <div className="bg-green-500 h-2.5 rounded-full" style={{width: '35%'}}></div> {/* Converted style */}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Low (0.35)</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">$0.95</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">18</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-green-500 font-medium">4.2%</div>
                      <div className="text-xs text-gray-500">Avg: 3.5%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Good</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button className="text-gray-600 hover:text-gray-900">View Details</button>
                  </td>
              </tr>
              <tr>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">bamboo yoga mat</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">3,800</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-24 bg-gray-200 rounded-full h-2.5">
                          <div className="bg-yellow-500 h-2.5 rounded-full" style={{width: '58%'}}></div> {/* Converted style */}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Medium (0.58)</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">$1.35</div></td>
                  <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">24</div></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-500 font-medium">2.1%</div>
                      <div className="text-xs text-gray-500">Avg: 3.2%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Underperforming</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-2">Optimize</button>
                      <button className="text-gray-600 hover:text-gray-900">View Details</button>
                  </td>
              </tr>
               <tr>
                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">smart fitness tracker</div></td>
                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">9,850</div></td>
                   <td className="px-6 py-4 whitespace-nowrap">
                       <div className="w-24 bg-gray-200 rounded-full h-2.5">
                           <div className="bg-red-500 h-2.5 rounded-full" style={{width: '78%'}}></div> {/* Converted style */}
                       </div>
                       <div className="text-xs text-gray-500 mt-1">High (0.78)</div>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">$1.92</div></td>
                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">38</div></td>
                   <td className="px-6 py-4 whitespace-nowrap">
                       <div className="text-sm text-red-500 font-medium">1.8%</div>
                       <div className="text-xs text-gray-500">Avg: 3.4%</div>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Underperforming</span>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       <button className="text-indigo-600 hover:text-indigo-900 mr-2">Optimize</button>
                       <button className="text-gray-600 hover:text-gray-900">View Details</button>
                   </td>
               </tr>
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">View all keywords</button>
          {/* Pagination */}
          <div className="flex items-center">
              <span className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">5</span> of <span className="font-medium">1,856</span> results
              </span>
              <div className="flex ml-4">
                  <button className="px-2 py-1 border border-gray-300 rounded-l-md text-sm font-medium bg-white text-gray-500 hover:bg-gray-50">
                      Previous
                  </button>
                  <button className="px-2 py-1 border border-gray-300 rounded-r-md text-sm font-medium bg-white text-gray-500 hover:bg-gray-50 ml-0 border-l-0">
                      Next
                  </button>
              </div>
          </div>
        </div>
      </div>

      {/* Recently Optimized Keywords Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-800">Recently Optimized Keywords</h3>
          </div>
          <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                      <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Before CTR</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current CTR</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Optimized On</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {/* Example Row 1 */}
                      <tr>
                          <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">noise cancelling earbuds</div></td>
                          <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">Wireless Earbuds Pro</div></td>
                          <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-red-500">1.5%</div></td>
                          <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-green-500">4.2%</div></td>
                          <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  +180%
                              </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">2 days ago</div></td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <button className="text-red-600 hover:text-red-900">Revert</button>
                          </td>
                      </tr>
                      {/* Add more example rows here */}
                       <tr>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">organic lip care</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">Organic Lip Balm</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-red-500">1.8%</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-green-500">3.5%</div></td>
                           <td className="px-6 py-4 whitespace-nowrap">
                               <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                   +94.4%
                               </span>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">3 days ago</div></td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               <button className="text-red-600 hover:text-red-900">Revert</button>
                           </td>
                       </tr>
                       <tr>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">fitness activity tracker</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">Fitness Tracker Band</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-red-500">2.1%</div></td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-green-500">2.9%</div></td>
                           <td className="px-6 py-4 whitespace-nowrap">
                               <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                   +38.1%
                               </span>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">5 days ago</div></td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               <button className="text-red-600 hover:text-red-900">Revert</button>
                           </td>
                       </tr>
                  </tbody>
              </table>
          </div>
      </div>
    </>
  );
};

export default KeywordMetricsPage;